import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "disabled", "indeterminate", "ariaLabel", "rightHandElement", "label", "optionBottomMargin", "onChange", "padding", "hasBackground", "isError", "checked", "defaultChecked", "rightPosition"];
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MinusIcon, TickIcon } from '@sainsburys-tech/bolt-icons';
import * as Styled from './Checkbox.styles';

var CheckboxItem = function CheckboxItem(_ref) {
  var id = _ref.id,
      disabled = _ref.disabled,
      indeterminate = _ref.indeterminate,
      ariaLabel = _ref.ariaLabel,
      rightHandElement = _ref.rightHandElement,
      label = _ref.label,
      optionBottomMargin = _ref.optionBottomMargin,
      onChange = _ref.onChange,
      padding = _ref.padding,
      hasBackground = _ref.hasBackground,
      isError = _ref.isError,
      checked = _ref.checked,
      defaultChecked = _ref.defaultChecked,
      rightPosition = _ref.rightPosition,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(checked !== null),
      _useState2 = _slicedToArray(_useState, 1),
      controlled = _useState2[0];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      firstRender = _useState4[0],
      setFirstRender = _useState4[1];

  var _useState5 = useState(controlled ? checked : defaultChecked),
      _useState6 = _slicedToArray(_useState5, 2),
      selected = _useState6[0],
      toggleSelected = _useState6[1]; // If uncontrolled, toggle on change


  var handleOnChange = function handleOnChange(checkboxId) {
    if (!controlled) toggleSelected(!selected);
    onChange(checkboxId);
  }; // If controlled, toggle on prop change


  useEffect(function () {
    if (!firstRender) {
      if (controlled) toggleSelected(checked);
    }
  }, [checked]);
  useEffect(function () {
    setFirstRender(false);
  }, []);
  var commonProps = {
    type: 'checkbox',
    key: "CheckboxList-checkbox-".concat(id),
    id: "".concat(id, "-checkbox"),
    name: "".concat(id, "-checkbox"),
    option: id,
    className: 'sr-only',
    onChange: function onChange() {
      return handleOnChange(id);
    },
    disabled: disabled,
    hasBackground: hasBackground,
    hasLabel: !!label,
    checked: selected
  };
  var NativeCheckbox = ariaLabel ? /*#__PURE__*/React.createElement(Styled.Checkbox, _extends({}, commonProps, {
    "aria-label": ariaLabel
  })) : /*#__PURE__*/React.createElement(Styled.Checkbox, _extends({}, commonProps, {
    "aria-labelledby": "".concat(id, "-label")
  }));
  return /*#__PURE__*/React.createElement(Styled.Label, _extends({
    id: "".concat(id, "-label"),
    "data-test": "".concat(id, "-link"),
    htmlFor: "".concat(id, "-checkbox"),
    key: "CheckboxList-label-".concat(id),
    optionBottomMargin: optionBottomMargin,
    disabled: disabled,
    hasBackground: hasBackground,
    hasLabel: !!label,
    padding: padding,
    "data-selected": selected
  }, props), NativeCheckbox, /*#__PURE__*/React.createElement("div", {
    className: "checkboxWrapper"
  }, /*#__PURE__*/React.createElement(Styled.CheckboxOption, null, isError ? /*#__PURE__*/React.createElement(Styled.SquareError, {
    "aria-hidden": true,
    as: indeterminate ? MinusIcon : TickIcon
  }) : /*#__PURE__*/React.createElement(Styled.Square, {
    "aria-hidden": true,
    as: indeterminate ? MinusIcon : TickIcon
  }), label && /*#__PURE__*/React.createElement(Styled.LabelWrapper, null, /*#__PURE__*/React.createElement(Styled.LabelValue, null, label)), rightHandElement && /*#__PURE__*/React.createElement(Styled.RightHandWrapper, {
    rightPosition: rightPosition
  }, rightHandElement))));
};

CheckboxItem.propTypes = {
  /**
   * 'label' is of type 'string' or a React Element.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * The required id of the checkbox.
   */
  id: PropTypes.string.isRequired,

  /**
   * 'rightHandElement' is a React element. It is vertically aligned to the centre by default,
   * but you can override this by adding an 'align-self` property to it.
   */
  rightHandElement: PropTypes.element,

  /**
   * Optional number to speciy how far the right hand element should be from the right.
   */
  rightPosition: PropTypes.number,

  /**
   * Optional boolean to speciy if the checkbox is a minus icon.
   */
  indeterminate: PropTypes.bool,

  /**
   * Optional boolean to speciy if the checkbox is disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Works the same as "defaultChecked" on an uncontrolled checkbox in React
   */
  defaultChecked: PropTypes.bool,

  /**
   * Works the same as "checked" on a controlled checkbox in React
   */
  checked: PropTypes.bool,

  /**
   * Optional prop to fire when an option is chosen.
   */
  onChange: PropTypes.func,

  /**
   * Optional prop to set how much padding should be around the checkbox.
   */
  padding: PropTypes.string,

  /**
   * Optional prop to set whether selected and hovered options should have a background
   */
  hasBackground: PropTypes.bool,

  /**
   * Optional prop to set red border on the checkbox
   */
  isError: PropTypes.bool,

  /**
   * Specify an optional data-test attribute to be added for e2e testing.
   */
  dataTest: PropTypes.string,

  /**
   * Optional prop to set aria label for screen readers, if missing defaults to using aria-labelledby
   */
  ariaLabel: PropTypes.string
};
CheckboxItem.defaultProps = {
  onChange: function onChange() {},
  optionBottomMargin: 5,
  dataTest: 'checkbox-item',
  padding: '10px',
  hasBackground: true,
  isError: false,
  defaultChecked: false,
  checked: null,
  rightPosition: 15
};
CheckboxItem.displayName = 'CheckboxItem';
export default CheckboxItem;